/**
 * Arbiter Sports API contracts.
 *
 * All endpoints live under `/v1/schools/:schoolId/integrations/arbiter/...`
 * and are gated to school-admins of the school + super-admins.
 *
 * Arbiter-side failures surface as a typed 502 envelope
 * (`ArbiterErrorEnvelope`); the web client maps the `kind` discriminator
 * to a specific banner.
 */

import { z } from 'zod'

import { ArbiterGameSchema, ArbiterTeamSchema, ArbiterTeamSectionLinkSchema } from '../../schemas/entities/arbiter'

// ----- GET /games -----

export const GetArbiterGamesRequestSchema = z.object({
  startDate: z.string().optional(),
  endDate: z.string().optional()
})

export type GetArbiterGamesRequest = z.infer<typeof GetArbiterGamesRequestSchema>

export const GetArbiterGamesResponseSchema = z.object({
  games: z.array(ArbiterGameSchema),
  startDate: z.string(),
  endDate: z.string()
})

export type GetArbiterGamesResponse = z.infer<typeof GetArbiterGamesResponseSchema>

// ----- GET /teams -----

export const GetArbiterTeamsResponseSchema = z.object({
  teams: z.array(ArbiterTeamSchema)
})

export type GetArbiterTeamsResponse = z.infer<typeof GetArbiterTeamsResponseSchema>

// ----- GET /team-section-links -----

export const ListArbiterTeamSectionLinksResponseSchema = z.object({
  links: z.array(ArbiterTeamSectionLinkSchema)
})

export type ListArbiterTeamSectionLinksResponse = z.infer<typeof ListArbiterTeamSectionLinksResponseSchema>

// ----- POST /team-section-links -----

export const CreateArbiterTeamSectionLinkRequestSchema = z.object({
  arbiterTeamId: z.number().int(),
  sectionPublicId: z.string().min(1)
})

export type CreateArbiterTeamSectionLinkRequest = z.infer<typeof CreateArbiterTeamSectionLinkRequestSchema>

export const CreateArbiterTeamSectionLinkResponseSchema = z.object({
  link: ArbiterTeamSectionLinkSchema,
  /** Soft-warn surface; populated when the link target wasn't in the latest getTeams() payload. */
  warning: z.string().optional()
})

export type CreateArbiterTeamSectionLinkResponse = z.infer<typeof CreateArbiterTeamSectionLinkResponseSchema>

// ----- PUT /team-section-links/:linkPublicId -----

export const UpdateArbiterTeamSectionLinkRequestSchema = z.object({
  sectionPublicId: z.string().min(1)
})

export type UpdateArbiterTeamSectionLinkRequest = z.infer<typeof UpdateArbiterTeamSectionLinkRequestSchema>

export const UpdateArbiterTeamSectionLinkResponseSchema = z.object({
  link: ArbiterTeamSectionLinkSchema
})

export type UpdateArbiterTeamSectionLinkResponse = z.infer<typeof UpdateArbiterTeamSectionLinkResponseSchema>

// ----- DELETE /team-section-links/:linkPublicId -----

export const DeleteArbiterTeamSectionLinkResponseSchema = z.object({
  success: z.literal(true)
})

export type DeleteArbiterTeamSectionLinkResponse = z.infer<typeof DeleteArbiterTeamSectionLinkResponseSchema>

// ----- GET /configured -----

export const GetArbiterConfiguredResponseSchema = z.object({
  configured: z.boolean(),
  env: z.enum(['prod', 'beta']).optional()
})

export type GetArbiterConfiguredResponse = z.infer<typeof GetArbiterConfiguredResponseSchema>
